import React, {useMemo} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {ReportsStackParamList} from '@/app/routes';
import {
  exportDepartmentAttendance,
  fetchDepartmentAttendance,
} from '@/entities/attendance/api';
import {
  currentMonth,
  periodKey,
  periodParam,
  type Period,
} from '@/entities/attendance/period';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {
  dailyTotals,
  rosterBreakdown,
  type IssueFilter,
} from '@/features/view-attendance/lib/aggregate';
import {reportFileName} from '@/features/view-attendance/lib/csv';
import {daysCsv, employeesCsv} from '@/features/view-attendance/lib/exports';
import {DailyChart} from '@/features/view-attendance/ui/DailyChart';
import {EmployeeReportList} from '@/features/view-attendance/ui/EmployeeReportList';
import {TimesheetPicker} from '@/features/export-timesheet/ui/TimesheetPicker';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {ReportToolbar} from '@/features/view-attendance/ui/ReportToolbar';
import {AttendanceKpi} from '@/features/view-attendance/ui/AttendanceKpi';
import {
  describePeriod,
  usePeriodLabel,
} from '@/features/view-attendance/lib/period-label';
import {ScheduleSummary} from '@/features/view-attendance/ui/ScheduleSummary';
import {enumParam, useParamState} from '@/shared/lib/use-param-state';
import {EmptyState} from '@/shared/ui/empty-state';
import {Page} from '@/shared/ui/page';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * One department for one period — the middle rung, reached from the department
 * comparison on the reports screen.
 *
 * It arrives with the period the parent was showing (a route param, not a fresh
 * default): drilling into a department must not silently change what is being looked
 * at. From here the period can be changed again, and the choice stays local — going
 * back leaves the overview exactly as it was.
 *
 * Unlike the company report this one ALWAYS has day rows: the department route does not
 * offer an `include_days` opt-out, because a department is already a bounded set of
 * people. So the trend chart and the detailed CSV need no switch here.
 */
type Props = NativeStackScreenProps<ReportsStackParamList, 'DepartmentReport'>;

const styles = StyleSheet.create({
  loading: {gap: spacing(3)},
});

/** Виды отбора, которые экран умеет положить в адрес. `''` — «все». */
const ISSUE_PARAM = enumParam(
  ['', 'late', 'absent', 'earlyLeave', 'silent', 'clean'] as const,
  '' as IssueFilter,
);

export function DepartmentReportScreen({navigation, route}: Props) {
  const {t, i18n} = useTranslation();
  const {label: periodTitle} = usePeriodLabel();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const {deptId, name} = route.params;

  const now = useMemo(() => new Date(), []);
  // Период — в адресе, ровно как на отчёте по сотруднику и по тем же доводам:
  // см. `EmployeeReportScreen` и `shared/lib/use-param-state`.
  const thisMonth = useMemo(() => currentMonth(now), [now]);
  const periodCodec = useMemo(() => periodParam(now), [now]);
  const [period, setPeriod] = useParamState<Period>('period', thisMonth, periodCodec);
  // A department IS its roster, so this one starts open — unlike the company report,
  // where the same section is a list of everybody and a scroll nobody asked for.
  /*
    Отбор таблицы — как на «Посещаемости»: им управляют плитки наверху, и он живёт в
    адресе, потому что «кто в этом отделе пропускал» — это ссылка.
  */
  const [issue, setIssue] = useParamState<IssueFilter>('issue', '', ISSUE_PARAM);

  const report = useQuery({
    queryKey: ['attendance-department', companyId, deptId, periodKey(period)],
    queryFn: () =>
      fetchDepartmentAttendance(companyId as string, deptId, period),
    enabled: !!companyId,
  });

  const days = report.data?.days ?? [];
  // Keyed on `report.data`, not on `days`: the `?? []` fallback is a new array on
  // every render and would make the memo recompute every time.
  const totals = useMemo(() => dailyTotals(report.data?.days ?? []), [report.data]);
  // Разложение отдела по людям — тот же смысл и тот же довод про ключ, что на
  // «Посещаемости»: см. `ReportsScreen`.
  const roster = useMemo(
    () => rosterBreakdown(report.data?.employees ?? []),
    [report.data],
  );

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.reports" />;
  }

  const openPerson = (row: {record_id: string; full_name: string}) =>
    navigation.navigate('EmployeeReport', {
      recordId: row.record_id,
      name: row.full_name,
      period: periodKey(period),
    });

  const title = report.data?.department.name ?? name;
  const periodLabel = periodTitle(report.data ?? describePeriod(period));
  const fileStem = reportFileName([t('reports.fileAttendance'), title, periodLabel], '').slice(
    0,
    -1,
  );

  let content: React.ReactNode;

  if (report.isPending) {
    content = (
      <View style={styles.loading}>
        <Skeleton height={190} radius={radii.xl} />
        <Skeleton height={140} radius={radii.xl} />
      </View>
    );
  } else if (report.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('reports.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void report.refetch()}
      />
    );
  } else {
    content = (
      <>
        <Section>
          {/* Та же шапка, что на «Посещаемости»: раньше отдел встречал фразой-вердиктом
              и полосой процентов, а его родитель — плитками, и переход между ними
              выглядел как переход между двумя разными продуктами. */}
          <AttendanceKpi
            summary={report.data.summary}
            roster={roster}
            issue={issue}
            onIssue={setIssue}
          />
        </Section>

        <Section>
          <ScheduleSummary
            schedule={
              report.data.schedule ?? report.data.department.attendance_schedule ?? null
            }
          />
        </Section>

        <Section>
          <DailyChart days={totals} />
        </Section>

        {/* Без раскрывателя: до людей доходили через два нажатия, из которых первое
            ничего не сообщало. */}
        <Section title={t('reports.byEmployee')}>
          <EmployeeReportList
            employees={report.data.employees}
            companyId={companyId}
            issue={issue}
            onIssueChange={setIssue}
            onOpen={openPerson}
          />
        </Section>
      </>
    );
  }

  return (
    <Page
      scroll
      title={title}
      subtitle={[t('reports.departmentSubtitle'), periodLabel || null]
        .filter(Boolean)
        .join('  \u00b7  ')}
      refreshing={report.isRefetching}
      onRefresh={() => void report.refetch()}
      testID="department-report"
      toolbar={
        <ReportToolbar
          period={period}
          onPeriodChange={setPeriod}
          now={now}
          periodLabel={periodLabel}
          exportSlot={
            <ExportBar
              fileName={fileStem}
              contents={t(
                days.length > 0 ? 'reports.exportDays' : 'reports.exportEmployees',
                {
                  period: periodLabel,
                  count:
                    days.length > 0 ? days.length : report.data?.employees.length ?? 0,
                },
              )}
              onServer={format =>
                exportDepartmentAttendance(companyId, deptId, period, format, i18n.language)
              }
              onLocal={() =>
                days.length > 0
                  ? daysCsv(days, t)
                  : employeesCsv(report.data?.employees ?? [], t)
              }
            />
          }
          /* Табель по одному отделу: `departmentIds` — тот же sv_control-идентификатор,
             которым ключуется сам отчёт, так что книга и экран говорят об одном. */
          timesheetSlot={
            <TimesheetPicker
              companyId={companyId}
              period={period}
              periodLabel={periodLabel}
              departmentIds={[deptId]}
              fileName={reportFileName(
                [t('timesheet.title'), title, periodLabel],
                '',
              ).slice(0, -1)}
              onCsv={() =>
                exportDepartmentAttendance(companyId, deptId, period, 'csv', i18n.language)
              }
            />
          }
        />
      }>
      {content}
    </Page>
  );
}
